import React, { memo, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import { validators } from 'investira.sdk';
import { Dialog, DialogContent, DialogTitle, DialogActions, Icon, Stack, LoadingButton, Divider, Button } from 'investira.react.components';
import { DatePicker, TextFieldCurrency, DecimalTextField } from '../../molecules';
import { useRecursoGarantidorContext } from '../../../contexts/RecursoGarantidorContext';
import { useCarteirasContext } from '../../../contexts/CarteirasContext';
import withResponseHandling from '../../../hoc/withResponseHandling';

/**
 * Renderiza o componente DialogAddRecursoGarantidor.
 *
 * @param {Object} props - Propriedades do componente.
 *
 * @description
 * Este componente realiza as seguintes operacoes:
 * 1. Recebe as propriedades necessarias para compor a interface
 * 2. Organiza os estados e callbacks usados durante a renderizacao
 * 3. Renderiza a experiencia visual do organismo
 *
 * @returns {JSX.Element|null} Elemento renderizado pelo componente.
 */

const DialogAddRecursoGarantidor = memo(props => {
  const {
    state,
    actions
  } = useRecursoGarantidorContext();
  const carteiraContext = useCarteirasContext();
  const isEdit = !validators.isEmpty(state.recursoSelected);
  const xInitialValues = {
    data: dayjs(),
    pl: 0,
    emprestimo: 0,
    imovel: 0,
    caixa: 0,
    provisoes: 0,
    cota: 0,
    demais_recursos: 0,
    carteira_id: ''
  };
  const xValidationSchema = Yup.object().shape({
    data: Yup.date().required('Data é obrigatória'),
    pl: Yup.number().required('PL é obrigatório'),
    emprestimo: Yup.number().required('Empréstimo é obrigatório'),
    imovel: Yup.number().required('Imóvel é obrigatório'),
    caixa: Yup.number().required('Caixa é obrigatório'),
    provisoes: Yup.number().required('Provisão é obrigatória'),
    demais_recursos: Yup.number().required('Demais Recursos é obrigatório'),
    ...(state.tipo === 'carteira' && {
      cota: Yup.number().required('Cota é obrigatório')
    })
  });
  const formik = useFormik({
    initialValues: xInitialValues,
    validationSchema: xValidationSchema,
    //enableReinitialize: true,
    onSubmit: (pValues, pActions) => {
      let xValues = {
        ...pValues
      };
      if (state.tipo === 'entidade' && !validators.isNull(pValues.data)) {
        const {
          cota,
          carteira_id,
          ...rest
        } = xValues;
        xValues = {
          ...rest,
          data: pValues.data.format()
        };
      }
      if (state.tipo === 'carteira') {
        const {
          carteira_id,
          data_posicao
        } = carteiraContext.state;
        xValues = {
          ...xValues,
          data: data_posicao,
          carteira_id: carteira_id
        };
      }
      const request = {
        add: actions.addRecurso,
        edit: actions.updateRecurso,
        merge: isEdit ? actions.updateRecurso : actions.addRecurso
      };

      // alterar se é add ou update por state.recursoSelected = vazio

      state.dialog && request[state.dialog]({
        tipo: state.tipo,
        ...xValues
      }, rRes => {
        let xPayload = {
          tipo: state.tipo
        };
        if (state.tipo === 'carteira') {
          const {
            carteira_id
          } = carteiraContext.state;
          xPayload = {
            ...xPayload,
            carteira_id: carteira_id
          };
        }
        actions.readRecursos(xPayload);
        actions.handleDialogChanged(null);
      }, null, () => {
        pActions.setSubmitting(false);
      });
    }
  });
  const handleChange = (pFieldName, pFieldValue) => {
    formik.setFieldValue(pFieldName, pFieldValue, true);
  };
  const handleClose = () => {
    formik.setSubmitting(false);
    formik.resetForm();
    actions.handleDialogClosed();
    actions.handleRecursoSelectedChanged({});
  };
  useEffect(() => {
    if (isEdit) {
      const {
        data,
        ...restValues
      } = state.recursoSelected;
      const xNewValues = Object.keys(restValues).reduce((acc, key) => {
        return {
          ...acc,
          [key]: restValues[key] !== null ? restValues[key] : xInitialValues[key]
        };
      }, {
        data: dayjs(data)
      });
      formik.setValues(xNewValues);
    }
    if (!isEdit) {
      formik.setValues({
        ...xInitialValues
      });
    }
  }, [state.tipo, state.recursoSelected, isEdit]);
  return <Dialog open={['add', 'edit', 'merge'].includes(state.dialog)} maxWidth="md" fullWidth onClose={handleClose}>
      <form onSubmit={formik.handleSubmit} noValidate>
        <DialogTitle>{isEdit ? `Editar Recurso` : `Adicionar Recurso`}</DialogTitle>
        <DialogContent sx={{
        paddingTop: '8px !important'
      }}>
          <Stack gap={3}>
            {state.dialog === 'add' && <>
                <Stack direction="row" flex="1">
                  <DatePicker label="Data" name="data" onChange={pValue => {
                handleChange('data', pValue);
              }} value={formik.values.data} fullWidth slotProps={{
                textField: {
                  error: Boolean(formik.errors.data),
                  helperText: formik.errors.data,
                  disabled: formik.isSubmitting,
                  variant: 'outlined'
                }
              }} />
                </Stack>
                <Divider />
              </>}
            <Stack direction="row" flex="1" gap={2}>
              <Stack flex="1">
                <TextFieldCurrency label="Recurso garantidor / Patrimônio líquido (PL)" name="pl" variant="outlined" type="number" value={formik.values.pl} onChange={(_, pValue) => handleChange('pl', Number(pValue))} error={Boolean(formik.errors.pl)} helperText={formik.errors.pl} fullWidth disabled={formik.isSubmitting} />
              </Stack>
              {state.tipo === 'carteira' && <Stack flex="1">
                  <DecimalTextField label="Cota" name="cota" id="cota" variant="outlined" value={formik.values.cota} placeholder={'100,00'} onChange={(_, pValue) => handleChange('cota', pValue)} allowNegative={false} decimal={8} InputLabelProps={{
                shrink: true
              }} error={Boolean(formik.errors.cota)} helperText={formik.errors.cota} disabled={formik.isSubmitting} fullWidth InputProps={{
                endAdornment: null
              }} />
                </Stack>}
            </Stack>

            <Stack direction="row" flex="1" gap={2}>
              <Stack flex="1">
                <TextFieldCurrency label="Caixa" name="caixa" type="number" variant="outlined" value={formik.values.caixa} onChange={(_, pValue) => handleChange('caixa', Number(pValue))} error={Boolean(formik.errors.caixa)} helperText={formik.errors.caixa} fullWidth disabled={formik.isSubmitting} />
              </Stack>
              <Stack flex="1">
                <TextFieldCurrency label="Provisões (-)" name="provisoes" type="number" variant="outlined" value={formik.values.provisoes * -1} onChange={(_, pValue) => handleChange('provisoes', Number(pValue))} error={Boolean(formik.errors.provisoes)} helperText={formik.errors.provisoes} fullWidth disabled={formik.isSubmitting} />
              </Stack>

              <Stack flex="1">
                <TextFieldCurrency label="Demais recursos" name="demais_recursos" type="number" variant="outlined" value={formik.values.demais_recursos} onChange={(_, pValue) => handleChange('demais_recursos', Number(pValue))} error={Boolean(formik.errors.demais_recursos)} helperText={formik.errors.demais_recursos} fullWidth disabled={formik.isSubmitting} />
              </Stack>
            </Stack>
            <Stack direction="row" flex="1" gap={2}>
              <Stack flex="1">
                <TextFieldCurrency label="Imóvel" name="imovel" type="number" variant="outlined" value={formik.values.imovel} onChange={(_, pValue) => handleChange('imovel', Number(pValue))} error={Boolean(formik.errors.imovel)} helperText={formik.errors.imovel} fullWidth disabled={formik.isSubmitting} />
              </Stack>
              <Stack flex="1">
                <TextFieldCurrency label="Empréstimo" name="emprestimo" type="number" variant="outlined" value={formik.values.emprestimo} onChange={(_, pValue) => handleChange('emprestimo', Number(pValue))} error={Boolean(formik.errors.emprestimo)} helperText={formik.errors.emprestimo} fullWidth disabled={formik.isSubmitting} />
              </Stack>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="primary" startIcon={<Icon iconName="cancel" size={16} />} size="small" sx={{
          px: 2
        }} type="button" onClick={() => actions.handleDialogClosed()} disabled={formik.isSubmitting}>
            Cancelar
          </Button>
          <LoadingButton variant="contained" color="primary" startIcon={<Icon iconName="save" size={16} />} size="small" sx={{
          px: 2
        }} type="submit" disabled={formik.isSubmitting || !formik.isValid} loading={formik.isSubmitting}>
            {/* {isEombait ? "Editar" : "Cadastrar"} */}
            Salvar
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>;
});
DialogAddRecursoGarantidor.displayName = 'DialogAddRecursoGarantidor';
export default withResponseHandling(DialogAddRecursoGarantidor);
